import cn from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { useState, useEffect, useMemo } from 'react';
import MeshGradient from 'react-scale/components/elements/webgl/mesh-gradient';

const HeroInput = ({
  title,
  pageName,
  formAction,
  searchSuggestions,
  formHiddenFields,
  webgl
}: {
  title;
  pageName;
  formAction;
  searchSuggestions: { text: string }[];
  formHiddenFields: { name: string; value: string }[];
  webgl: {
    style: {
      '--gradient-color-1': string;
      '--gradient-color-2': string;
      '--gradient-color-3': string;
      '--gradient-color-4': string;
    };
  };
}) => {
  const [inputValue, setInputValue] = useState('');
  const [inputIsFocused, setInputIsFocused] = useState(false);
  const [isUsingRecommendations, setIsUsingRecommendations] = useState(false);
  const [focusedSuggestion, setFocusedSuggestion] = useState<{ text: string }>(undefined);

  const suggestionsThatMatchInputValue = useMemo(() => {
    if (!inputValue) return searchSuggestions;

    return searchSuggestions.filter(suggestion => suggestion.text.includes(inputValue));
  }, [inputValue]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFocusedSuggestion(undefined);
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedSuggestion((prevFocused: any) => {
          if (!prevFocused) return suggestionsThatMatchInputValue[0];

          const index = suggestionsThatMatchInputValue.indexOf(prevFocused);

          if (index === -1 || index === suggestionsThatMatchInputValue.length - 1)
            return prevFocused;

          return suggestionsThatMatchInputValue[index + 1];
        });
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedSuggestion((prevFocused: any) => {
          const index = suggestionsThatMatchInputValue.indexOf(prevFocused);

          if (index === -1 || index === 0) return undefined;

          return suggestionsThatMatchInputValue[index - 1];
        });
      }

      if (e.key === 'Enter' && focusedSuggestion) {
        window.open(
          `${formAction}?${formHiddenFields.reduce(
            (acum, current) => acum + `&${current.name}=${current.value}`,
            ``
          )}&similar_text_query=${focusedSuggestion.text}`,
          '_blank'
        );

        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [focusedSuggestion, suggestionsThatMatchInputValue]);

  useEffect(() => {
    if (suggestionsThatMatchInputValue.length < 1) return;
    if (suggestionsThatMatchInputValue.includes(focusedSuggestion)) return;
    if (!focusedSuggestion) return;

    setFocusedSuggestion(suggestionsThatMatchInputValue[0]);
  }, [suggestionsThatMatchInputValue]);

  return (
    <div className="relative">
      <div className="absolute inset-0 z-0 bg-black pointer-events-none">
        <MeshGradient style={webgl.style} />
        <div className="absolute inset-0 w-full bg-gradient-to-b from-black" />
        <div className="absolute inset-0 w-full bg-gradient-to-b from-black" />
      </div>
      <div className="container relative py-20 lg:py-32 xl:pb-36 xl:pt-40">
        <h1 className="text-white font-aeonik text-4xl sm:text-5xl md:text-6xl xl:text-6.5xl text-center mb-10">
          {title}
        </h1>
        <div className="relative max-w-3xl mx-auto">
          <form
            target="_blank"
            className="flex flex-col space-y-4 md:space-x-4 md:flex-row md:space-y-0"
            action={formAction}
          >
            <div
              className={cn(
                inputIsFocused ? 'text-white' : 'text-neutral-500',
                'relative flex-1 flex hover:text-white'
              )}
            >
              <svg
                className="absolute flex -translate-y-1/2 left-4 md:left-6 top-1/2"
                width="24"
                height="24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <input
                autoComplete="off"
                onChange={e => setInputValue(e.target.value)}
                onFocus={() => setInputIsFocused(true)}
                onBlur={() => {
                  setFocusedSuggestion(undefined);
                  setInputIsFocused(false);
                }}
                value={inputValue}
                placeholder="Search ImageNet here"
                className="w-full px-12 py-4 text-white border rounded-lg md:py-3 border-tundora bg-neutral-800 md:px-14 focus:outline-none hover:placeholder-white focus:placeholder-white hover:border-neutral-600 focus:border-neutral-600"
                name="similar_text_query"
              />
              {formHiddenFields.map((field, index) => (
                <input key={index} type="hidden" name={field.name} value={field.value} />
              ))}
              {inputValue && (
                <button
                  type="button"
                  className="absolute flex -translate-y-1/2 right-4 md:right-7 top-1/2"
                  onClick={() => setInputValue('')}
                >
                  <Image
                    alt="Close"
                    src="/static/images/global/icons/cross.svg"
                    loading="eager"
                    width={16}
                    height={16}
                  />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="bg-gradient-x p-[1px] border border-tundora rounded-lg md:rounded-full space-x-2 focus:bg-none focus:outline-white transition-colors duration-700"
            >
              <div className="flex items-center justify-center p-3 space-x-2 bg-black rounded-lg md:rounded-full">
                <Image
                  src="/static/images/icons/products/nucleus.svg"
                  loading="eager"
                  width={30}
                  height={30}
                  alt="Nucleus"
                />
                {pageName && <span className="md:hidden">{pageName}</span>}
              </div>
            </button>
          </form>
          {(inputIsFocused || isUsingRecommendations) && suggestionsThatMatchInputValue.length > 0 && (
            <div
              className="absolute block w-full overflow-hidden border rounded-lg bg-neutral-800 border-tundora top-18"
              onMouseOver={() => setIsUsingRecommendations(true)}
              onMouseLeave={() => setIsUsingRecommendations(false)}
            >
              <p className="px-6 py-4 text-sm border-b text-neutral-400 border-tundora">
                Example queries
              </p>
              {suggestionsThatMatchInputValue.map(suggestion => (
                <Link
                  key={suggestion.text}
                  href={`${formAction}?${formHiddenFields.reduce(
                    (acum, current) => acum + `&${current.name}=${current.value}`,
                    ``
                  )}&similar_text_query=${suggestion.text}`}
                  target="_blank"
                  className={cn(
                    focusedSuggestion?.text === suggestion.text
                      ? 'bg-utility-orange text-white'
                      : 'text-neutral-400 hover:bg-neutral-900',
                    'block px-6 py-4 hover:text-white'
                  )}
                >
                  {suggestion.text}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroInput;
